import React, {useContext} from 'react'
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    TextField,
    Typography,
} from '@mui/material'
import {Box} from '@mui/system'
import ControlContainer from '../ControlContainer'
import {AppContext} from '../../contexts/AppContextProvider'
import {GeneralInfoState} from '../../contexts/types'
import {useForm} from '../../hooks/useForm'
import useScreenFlow from '../../hooks/useScreenFlow'
import useAddresses from '../../hooks/useAddresses'
import useLogger from '../../hooks/useLogger'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import OrgMappedImage from "../OrgMappedImage";
import {updateGeneralInfo} from "../../contexts/GeneralInfoReducer";

const NatureOfComplaint: React.FC = () => {
    const {navigateTo} = useScreenFlow()
    const {getPrimaryAddress} = useAddresses()
    const shippingAddress = getPrimaryAddress()
    const log = useLogger()
    const {customer, complaint, dispatch, loggedIn, postNotification, clearNotification, countryCode} =
        useContext(AppContext)
    const {t, language} = useContext(OrgCustomizationContext)
    const generalInfo = complaint.generalInfo
    const {
        data,
        errors,
        handleChange,
        handleChangeEvent,
        validateFields,
        enableOnChangeValidation,
    } = useForm<GeneralInfoState>({
        initialValues: {
            ...complaint.generalInfo,
            contactName: !complaint.generalInfo.contactName
                ? customer.firstName + ' ' + customer.lastName
                : complaint.generalInfo.contactName,
        },
        validations: {
            isAcknowledged: {
                required: {
                    value: true,
                    message: t('this_field_is_required'),
                },
            },
        },
    })

    // This line determines which screen is loaded next
    const navigateToNextScreen = () => navigateTo('/productsearch')//navigateForward()

    const greetingName = generalInfo.contactName
        ? generalInfo.contactName
        : customer.firstName + ' ' + customer.lastName

    const handleSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        if (!data.contactName.trim()) {
            data.contactName = customer.firstName + ' ' + customer.lastName
        }

        dispatch(updateGeneralInfo({...data}))

        navigateToNextScreen()

        log.email('logged from Nature of Complaint')
    }

    return (
        <>
            <form onSubmit={handleSubmitForm}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant='h5'>
                            {
                                 t('hello', {greetingName}) 
                                
                            }
                            {countryCode === 'jp' && language === 'ja' && (loggedIn ? ' 様' : ` ${t('sensei')}`)}
                        </Typography>

                        {loggedIn ?
                            <>
                                <Box
                                    sx={{
                                        border: '1px solid lightgray',
                                        margin: '1.5rem 0',
                                        padding: '1rem',
                                        maxWidth: '350px',
                                    }}
                                >
                                    <Typography variant='body2' component='div'>
                                        {customer.name1}
                                        <br/>
                                        {shippingAddress?.street} <br/>
                                        {shippingAddress?.city} <br/>
                                        {shippingAddress?.region && (
                                            <>
                                                {shippingAddress.region} <br/>
                                            </>
                                        )}
                                        {shippingAddress?.postalCode} <br/>
                                        {shippingAddress?.country}
                                    </Typography>

                                </Box>

                                <FormControl
                                    error={!!errors.contactName}
                                    sx={{mb: 2}}
                                >
                                    <FormLabel sx={{mb: 1}}>
                                        {t('noc_not_name', {
                                            contactName: greetingName,
                                        })}
                                    </FormLabel>
                                    <TextField
                                        id='contact-name'
                                        error={
                                            typeof errors.contactName !== 'undefined'
                                        }
                                        size='small'
                                        sx={{mb: 3, mr: 'auto'}}
                                        helperText={errors.contactName}
                                        value={data.contactName}
                                        onChange={handleChangeEvent('contactName')}
                                    />
                                </FormControl>
                            </>
                            : <Box sx={{height: '2rem'}}/>
                        }
                        <Typography variant='h5'>
                            {t('noc_nature_of_complaint')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={7} sx={{border: 0}}>
                        <Typography variant='body1' component='div'>
                            <p>{t('noc_paragraph_1')}</p>
                            <Typography color='primary' component='span'>
                                <p>{t('noc_paragraph_2')}</p>
                            </Typography>
                            <p>{t('noc_paragraph_3')}</p>
                        </Typography>

                        <FormControl
                            component='fieldset'
                            sx={{display: {xs: 'none', md: 'flex'}}}
                        >
                            <FormGroup>
                                <FormControlLabel
                                    style={{display: 'table'}}
                                    control={
                                        <div style={{display: 'table-cell'}}>
                                            <Checkbox
                                                color='primary'
                                                checked={data.isAcknowledged}
                                                onChange={(e) => {
                                                    handleChange(
                                                        'isAcknowledged',
                                                        !data.isAcknowledged
                                                    )
                                                }}
                                            />
                                        </div>
                                    }
                                    label={t('noc_acknowledgement') as string}
                                />
                                <FormHelperText error={!!errors.isAcknowledged}>
                                    {errors.isAcknowledged}
                                </FormHelperText>
                            </FormGroup>
                        </FormControl>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={5}
                        sx={{
                            border: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'bottom',
                            mt: {xs: 0, md: 3},
                        }}
                    >
                        <OrgMappedImage imageKey='ProductLabel'/>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={7}
                        sx={{
                            display: {xs: 'flex', md: 'none'},
                            mt: 1,
                        }}
                    >
                        <FormControl component='fieldset'>
                            <FormGroup>
                                <FormControlLabel
                                    style={{display: 'table'}}
                                    control={
                                        <div style={{display: 'table-cell'}}>
                                            <Checkbox
                                                color='primary'
                                                checked={data.isAcknowledged}
                                                onChange={(e) => {
                                                    handleChange(
                                                        'isAcknowledged',
                                                        !data.isAcknowledged
                                                    )
                                                }}
                                            />
                                        </div>
                                    }
                                    label={t('noc_acknowledgement') as string}
                                />
                                <FormHelperText error={!!errors.isAcknowledged}>
                                    {errors.isAcknowledged}
                                </FormHelperText>
                            </FormGroup>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <ControlContainer>
                            <Button
                                size='large'
                                variant='contained'
                                color='primary'
                                // onClick={handleSubmitForm}
                                type='submit'
                            >
                                {t('continue')}
                            </Button>
                        </ControlContainer>
                    </Grid>
                </Grid>
            </form>
        </>
    )
}

export default NatureOfComplaint
